import Button from '@/components/Buttons/Button';
import { Dropdown } from '@/components/Dropdown/Dropdown';
import { ListItemIcon, ListItemText, MenuItem } from '@/components/Dropdown/MenuItems/components';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import AdornmentSelect from '@/components/Selects/AdornmentSelect';
import { ThreeDotsCircle } from '@/components/ThreeDotsCircle/ThreeDotsCircle';
import DownloadIcon from '@/images/DownloadMinimalistic.svg';
import ShareIcon from '@/images/Share.svg';
import { useGetAgents } from '@/pages/agents/api';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import { IAgent } from '@d-id/types';
import styled from '@emotion/styled';
import { Alert, Divider, Popover, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useAgentInsightsContext } from '../../contex/useAgentInsightsContext';
import { useExportChats } from '../../hooks/useExportChats';
import { ALL_AGENTS, INSIGHTS_DATE_RANGE } from '../../types';
import { ExportConversationsModal } from '../ExportConversationsModal';
import { ShareDashboardModal } from '../ShareDashboardModal';
import { BackButton } from './BackButton';

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 32px;
`;

const TopRow = styled.div`
    display: flex;
    align-items: center;
`;

const HeaderTitle = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary.main};
    font-weight: 600;
    font-size: 18px;
    margin-right: 32px;
`;

const AlertContainer = styled.div`
    width: 100%;
`;

const TimeFilterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-left: auto;
    gap: 4px;
    height: 40px;
    padding: 4px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.black[300]};
    background-color: ${theme.colors.white};
`;

const TimeFilterButton = styled(Button)<{ active: boolean }>`
    padding: 0 12px;
    border-radius: 8px;
    height: 32px;
    min-height: 32px;

    &.MuiButtonBase-root {
        background-color: ${({ active }) => (active ? theme.colors.black[100] : theme.colors.white)};

        &:hover {
            background-color: ${({ active }) => (active ? theme.colors.black[100] : theme.colors.black[50])}!important;
        }
    }
`;

const StyledAdornmentSelect = styled(AdornmentSelect<IAgent | null>)`
    & .MuiInputBase-root {
        padding: 1px 39px 1px 12px;
    }

    & .MuiInputBase-input {
        font-size: 16px;
        cursor: pointer;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-radius: 8px;
        border-color: ${theme.colors.black[300]};
    }
`;

const DateRangePopoverContent = styled.div`
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 280px;
`;

const DateRangeRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DateLabel = styled(Typography)`
    font-size: 14px;
    font-weight: 500;
    color: ${theme.colors.secondary.main};
`;

const StyledTextField = styled(TextField)`
    & .MuiInputBase-root {
        background: ${theme.colors.white};
        height: 40px;
        cursor: pointer;
    }

    & input {
        padding: 8px 12px;
        font-size: 14px;
        cursor: pointer;
    }

    & .MuiOutlinedInput-notchedOutline {
        border-radius: 8px;
        border-color: ${theme.colors.black[300]};
    }

    &:hover .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.colors.primary[500]};
    }
`;

const DateRangeActions = styled.div`
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 8px;
`;

const DateRangeButton = styled(Button)`
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 14px;
    height: 36px;
    min-width: 80px;
`;

const MenuButton = styled.div`
    margin-left: 12px;
`;

const StyledMenuItem = styled(MenuItem)`
    height: 40px;
    padding: 8px 12px;
`;

export const InsightsHeader = () => {
    const mixpanel = useAnalytics();
    const { dateRange, setDateRange, agent, setAgent, isConversationView, from, to } = useAgentInsightsContext();
    const { error, clearError } = useExportChats();
    const [dateRangeAnchor, setDateRangeAnchor] = useState<HTMLButtonElement | null>(null);
    const [customStartDate, setCustomStartDate] = useState<string>('');
    const [customEndDate, setCustomEndDate] = useState<string>('');
    const startDateInputRef = useRef<HTMLInputElement>(null);
    const endDateInputRef = useRef<HTMLInputElement>(null);
    const shareInsightsEnabled = useFlag('share-insights-enabled', false);
    const [isMenuClosed, setIsMenuClosed] = useState(true);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    const { isAnonymous } = useAuth();
    const { data } = useGetAgents(isAnonymous);
    const agentsWithoutTemplates = data?.items.filter(a => a.access !== 'public');
    const agentList = [ALL_AGENTS, ...(agentsWithoutTemplates || [])];

    const handleTimeRangeChange = (range: INSIGHTS_DATE_RANGE, event?: React.MouseEvent<HTMLButtonElement>) => {
        mixpanel?.track?.('agents-insights', { event: 'click', label: 'Insights', dateFilter: range });
        clearError();

        if (range === INSIGHTS_DATE_RANGE.CUSTOM && event) {
            setDateRangeAnchor(event.currentTarget);
            setCustomStartDate(from.toISOString().split('T')[0]);
            setCustomEndDate(to.toISOString().split('T')[0]);
        } else {
            setDateRangeAnchor(null);
            setDateRange(range);
        }
    };

    const handleAgentChange = (agent: IAgent | null) => {
        mixpanel?.track?.('agents-insights', { event: 'click', label: 'Insights', agent: agent, dateFilter: dateRange });
        setAgent(agent ?? ALL_AGENTS);
    };

    const handleApplyCustomDates = () => {
        clearError();

        // Use epoch date if start date is empty
        const fromDate = customStartDate ? new Date(customStartDate) : new Date('1970-01-01');
        fromDate.setHours(0, 0, 0, 0);

        // Use today if end date is empty
        const toDate = customEndDate ? new Date(customEndDate) : new Date();
        toDate.setHours(23, 59, 59, 999);

        setDateRange(INSIGHTS_DATE_RANGE.CUSTOM, fromDate, toDate);
        setDateRangeAnchor(null);
    };

    const handleCancelCustomDates = () => {
        setDateRangeAnchor(null);
    };

    const handleMenuClose = () => {
        setIsMenuClosed(true);
    };

    const handleExportClick = () => {
        setIsExportModalOpen(true);
        handleMenuClose();
    };

    const handleShareClick = () => {
        setIsShareModalOpen(true);
        handleMenuClose();
    };

    const getCustomDateLabel = () => {
        if (dateRange !== INSIGHTS_DATE_RANGE.CUSTOM) {
            return 'Custom';
        }
        const fromFormatted = from.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const toFormatted = to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${fromFormatted} - ${toFormatted}`;
    };

    return (
        <HeaderContainer>
            <TopRow>
                {isConversationView && <BackButton text="Conversation" />}
                {!isConversationView && <HeaderTitle>Insights</HeaderTitle>}

                <StyledAdornmentSelect
                    id="agent-select"
                    label=""
                    options={agentList}
                    getOptionLabel={option => option?.preview_name || ''}
                    value={agent}
                    onChange={(_, value) => handleAgentChange(value)}
                    className="options-select"
                    optionSx={{ fontWeight: 400 }}
                    paperSx={{ borderRadius: '8px' }}
                    disableClearable={true}
                />

                <TimeFilterContainer>
                    <TimeFilterButton
                        active={dateRange === INSIGHTS_DATE_RANGE.LAST_6_MONTHS}
                        onClick={() => handleTimeRangeChange(INSIGHTS_DATE_RANGE.LAST_6_MONTHS)}>
                        6M
                    </TimeFilterButton>

                    <Divider orientation="vertical" flexItem />

                    <TimeFilterButton
                        active={dateRange === INSIGHTS_DATE_RANGE.LAST_30_DAYS}
                        onClick={() => handleTimeRangeChange(INSIGHTS_DATE_RANGE.LAST_30_DAYS)}>
                        30D
                    </TimeFilterButton>

                    <Divider orientation="vertical" flexItem />

                    <TimeFilterButton
                        active={dateRange === INSIGHTS_DATE_RANGE.LAST_7_DAYS}
                        onClick={() => handleTimeRangeChange(INSIGHTS_DATE_RANGE.LAST_7_DAYS)}>
                        7D
                    </TimeFilterButton>

                    <Divider orientation="vertical" flexItem />

                    <TimeFilterButton
                        active={dateRange === INSIGHTS_DATE_RANGE.YESTERDAY}
                        onClick={() => handleTimeRangeChange(INSIGHTS_DATE_RANGE.YESTERDAY)}>
                        Yesterday
                    </TimeFilterButton>

                    <Divider orientation="vertical" flexItem />

                    <TimeFilterButton
                        active={dateRange === INSIGHTS_DATE_RANGE.CUSTOM}
                        onClick={e => handleTimeRangeChange(INSIGHTS_DATE_RANGE.CUSTOM, e)}>
                        {getCustomDateLabel()}
                    </TimeFilterButton>
                </TimeFilterContainer>

                {!isConversationView && (
                    <MenuButton>
                        <Dropdown
                            closed={isMenuClosed}
                            width="220px"
                            onOpen={() => setIsMenuClosed(false)}
                            onClose={handleMenuClose}
                            trigger={<ThreeDotsCircle style={{ width: '40px', height: '40px' }} isActive={!isMenuClosed} />}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            items={[
                                ...(shareInsightsEnabled
                                    ? [
                                          {
                                              label: (
                                                  <StyledMenuItem onClick={handleShareClick}>
                                                      <ListItemIcon>
                                                          <MaskedIcon src={ShareIcon} size="20px" color={theme.colors.secondary[900]} />
                                                      </ListItemIcon>
                                                      <ListItemText>Share dashboard</ListItemText>
                                                  </StyledMenuItem>
                                              ),
                                          },
                                      ]
                                    : []),

                                {
                                    label: (
                                        <StyledMenuItem onClick={handleExportClick}>
                                            <ListItemIcon>
                                                <MaskedIcon src={DownloadIcon} size="20px" color={theme.colors.secondary[900]} />
                                            </ListItemIcon>
                                            <ListItemText>Export conversations</ListItemText>
                                        </StyledMenuItem>
                                    ),
                                },
                            ]}
                        />
                    </MenuButton>
                )}

                <Popover
                    open={!!dateRangeAnchor}
                    anchorEl={dateRangeAnchor}
                    onClose={handleCancelCustomDates}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                marginTop: 1,
                                borderRadius: '8px',
                                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                            },
                        },
                    }}>
                    <DateRangePopoverContent>
                        <DateRangeRow>
                            <DateLabel>Start Date</DateLabel>
                            <StyledTextField
                                type="date"
                                value={customStartDate}
                                onChange={e => setCustomStartDate(e.target.value)}
                                onClick={() => startDateInputRef.current?.showPicker?.()}
                                inputRef={startDateInputRef}
                                size="small"
                                fullWidth
                            />
                        </DateRangeRow>
                        <DateRangeRow>
                            <DateLabel>End Date</DateLabel>
                            <StyledTextField
                                type="date"
                                value={customEndDate}
                                onChange={e => setCustomEndDate(e.target.value)}
                                onClick={() => endDateInputRef.current?.showPicker?.()}
                                inputRef={endDateInputRef}
                                size="small"
                                fullWidth
                            />
                        </DateRangeRow>
                        <DateRangeActions>
                            <DateRangeButton variant="outlined" color="secondary" onClick={handleCancelCustomDates}>
                                Cancel
                            </DateRangeButton>
                            <DateRangeButton variant="contained" color="primary" onClick={handleApplyCustomDates}>
                                Apply
                            </DateRangeButton>
                        </DateRangeActions>
                    </DateRangePopoverContent>
                </Popover>
            </TopRow>

            {error && (
                <AlertContainer>
                    <Alert severity="error">{error}</Alert>
                </AlertContainer>
            )}

            {isExportModalOpen && <ExportConversationsModal onClose={() => setIsExportModalOpen(false)} />}
            {isShareModalOpen && <ShareDashboardModal onClose={() => setIsShareModalOpen(false)} />}
        </HeaderContainer>
    );
};
